/**
 * XXX: This is meant to be imported from `match-booster/types but the app
 * typecheck will fail since backend code doesn't have strict null check.
 * For now, define the SemanticColumnType here as we don't expect this to
 * change frequently
 */
export enum SemanticColumnType {
  Email = "email",
  Phone = "phone",
  PhoneMd5 = "phone_md5",
  PhoneSha256 = "phone_sha256",
  MaidAaid = "AAID",
  MaidIdfa = "IDFA",
  MobileID = "MAID",
  IpAddress = "ip_address",
  EmailMd5 = "email_md5",
  EmailSha256 = "email_sha256",
  FirstName = "first_name",
  LastName = "last_name",
  Zip = "zip",
  City = "city",
  LgID = "lg_id",
  Street = "street",
  Street2 = "street2",
  State = "state",
  FirstNameSha256 = "first_name_sha256",
  LastNameSha256 = "last_name_sha256",
  StreetSha256 = "street_sha256",
  Street2Sha256 = "street2_sha256",
  CitySha256 = "city_sha256",
  StateSha256 = "state_sha256",
  ZipSha256 = "zip_sha256",
}

export type BaseType = {
  type: string;
  /**
   * `true` if value cannot be `undefined`.
   * @default false
   */
  required?: boolean;
  /**
   * `true` if value cannot be `null`.
   * @default false
   */
  notNull?: boolean;
  /**
   * `true` if the type contains metadata for retrieving
   * remote context for validation, data-coersion, etc.
   * @default false
   */
  async?: boolean;

  // @presentation non-functional metadata
  // these are piece of information no used for validation or data coercion
  // but rather only for things like data presentation
  /**
   * @presentation
   */
  label?: string;
  /**
   * @presentation
   */
  description?: string;

  identifier?: boolean;

  /**
   * `true` means we cannot write to this field.
   * This is useful for declaring readonly identifier fields that are used for record-matching.
   */
  readonly?: boolean;

  /* Used for auto-mapping with match boosted columns */
  semanticColumnType?: SemanticColumnType;

  /**
   * By default, all MB columns are mapped but this allows user to remove
   * specified columns for special cases (e.g. TTD only allows one identifier
   * type per segment so a user can choose which boosted column to map here)
   */
  matchBoosterOptional?: boolean;
};

export type Props<T extends BaseType> = Readonly<Omit<T, "type" | "async">>;

export const validator: ValidatorSync<BaseType> =
  (type) => (value: unknown) => {
    if (type.required && value === undefined) {
      throw new Error(`undefined value is not allowed`);
    }
    if (type.notNull && value === null) {
      throw new Error(`null value is not allowed`);
    }
  };

/**
 * Options for extended-type validators.
 * `extendedTypesValidationsV2` selects hand-rolled string/number/boolean/object
 * validators instead of Yup. Evaluated at validator-build time, not per value.
 */
export type ExtendedTypeValidatorOptions = {
  extendedTypesValidationsV2?: boolean;
};

const objectToString = Object.prototype.toString;

/**
 * Plain `[object Object]` check for hand-rolled object validation (v2).
 * Dates, arrays, functions, and other host objects fail.
 * Added to match Yup validateSync type-check behavior.
 */
export function isPlainObject(value: unknown): boolean {
  return objectToString.call(value) === "[object Object]";
}

/**
 * Yup `mixed.notType` with `strict: true`. The `(cast from the value …)` suffix
 * appears for NaN because `NaN !== NaN` makes Yup think a cast occurred.
 * Added to match Yup validateSync error formatting behavior.
 */
export function typeMismatchError(typeName: string, value: unknown): Error {
  const printed = formatValueForTypeError(value);
  const isCast = value != null && value !== value;
  const suffix = isCast ? ` (cast from the value \`${printed}\`).` : ".";
  return new Error(
    `this must be a \`${typeName}\` type, but the final value was: \`${printed}\`${suffix}`,
  );
}

/** Added to match Yup validateSync error formatting behavior. */
function formatValueForTypeError(value: unknown): string {
  if (value == null || value === true || value === false) {
    return String(value);
  }

  if (typeof value === "number") {
    if (value !== value) return "NaN";
    if (value === 0 && 1 / value < 0) return "-0";
    return String(value);
  }
  if (typeof value === "string") return `"${value}"`;
  if (typeof value === "function") {
    return `[Function ${value.name || "anonymous"}]`;
  }
  if (typeof value === "symbol") {
    return value.toString().replace(/^Symbol\((.*)\)(.*)$/, "Symbol($1)");
  }

  const tag = objectToString.call(value).slice(8, -1);
  if (tag === "Date") {
    const date = value as Date;
    return Number.isNaN(date.getTime()) ? String(date) : date.toISOString();
  }
  if (tag === "Error" || value instanceof Error) {
    return `[${Error.prototype.toString.call(value)}]`;
  }
  if (tag === "RegExp") return String(value);

  return JSON.stringify(value, undefined, 2) ?? String(value);
}

/**
 * Generates syncrounous validator.
 */
export type ValidatorSync<T extends BaseType> = (
  type: Readonly<T>,
  options?: ExtendedTypeValidatorOptions,
) => (value: unknown) => void;

/**
 * Generates async validator.
 */
export type Validator<T extends BaseType> = (
  type: Readonly<T>,
) => (value: unknown) => Promise<void>;
